import { getWebPath } from '@doss/common-all'
import { Box, Theme } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { isNil } from 'lodash'
import { useEffect, useRef } from 'react'
import { FiExternalLink } from 'react-icons/fi'
import { Else, If, Then } from 'react-if'
import {
  BreadcrumbObjectType,
  useGetFileCellDocumentLazyQuery,
} from '~/common/generated/apollo-operations'
import useStyles from '~/common/hooks/useStyles'
import { TEXT_LENGTH_LIMIT } from '~/common/utils/table'
import { DetailedTableColumn, DossTableCell, TableType } from '~/components/tables/types'
import { useGlobalContext } from '../../common/hooks/useGlobalContext'
import { ActiveUser } from '../settings/types'
import { TableSource } from '../shared/DossTable'
import Link from '../shared/Link'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'
import { FileChip } from '../ui/molecules/file-chip/FileChip'
import { SignatureChip } from '../ui/molecules/file-chip/SignatureChip'
import { FileInlineImage } from './FileTableCells/FileInlineImage'
import { FormattedTableCellComponent } from './FormattedTableCell'

dayjs.extend(utc)

const TEXT_WRAP_ROW_LIMIT = 3

export function getTableCellStyles(theme: Theme) {
  return {
    // Styling for the cells in the first column
    indexCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      linkOut: {
        display: 'flex',
        color: theme.palette.common.doss.blue[10],
        alignItems: 'center',
        fontSize: '12px',
      },
      textClickable: {
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
      },
    },

    textWrapping: {
      paddingBlock: theme.spacing(1),
      paddingInline: theme.spacing(0),
      whiteSpace: 'pre-line',
      wordWrap: 'break-word',
      display: '-webkit-box',
      boxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineClamp: TEXT_WRAP_ROW_LIMIT,
      lineHeight: '1.25em',
      maxHeight: `${1.5 * TEXT_WRAP_ROW_LIMIT}em`,
    },

    // Styling for the checkbox to always be center aligned
    checkbox: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },

    // Styling for numbers to always be right aligned
    number: {
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-end',
    },

    // Styling for formulas to have number on the right and copy on the left
    formula: {
      alignSelf: 'center',
    },
    autogeneratedIdCell: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
  }
}

interface TableCellComponentProps {
  tableColumnHeader: DetailedTableColumn
  cell: DossTableCell
  isFirstColumn: boolean
  value: string[] | string | boolean | number | null | undefined
  rowId: string
  maxColWidth: number
  tableType: TableType
  showAutogeneratedIdTooltip?: boolean
  readOnly: boolean
  activeUsers: ActiveUser[]
  tableSource: TableSource
}

/**
 * Renders a table cell inside a Doss table
 */
export function TableCellComponent({
  maxColWidth,
  rowId,
  cell,
  value,
  tableColumnHeader,
  isFirstColumn,
  tableType,
  showAutogeneratedIdTooltip = false,
  readOnly,
  activeUsers,
  tableSource,
}: TableCellComponentProps) {
  const styles = useStyles(getTableCellStyles)
  const { user } = useGlobalContext()
  const documentIdRef = useRef<string | null>(null)
  const [getFile, { data: fileCellDocumentData }] = useGetFileCellDocumentLazyQuery()

  useEffect(() => {
    if (cell.documents.length > 0) {
      documentIdRef.current = cell.documents[0].id
      if (documentIdRef.current) {
        getFile({ variables: { documentId: documentIdRef.current } })
      }
    }
  }, [cell, getFile])

  if (isNil(cell)) {
    return null
  }

  let fileContent = null
  if (documentIdRef.current && fileCellDocumentData) {
    if (fileCellDocumentData.document.signature) {
      fileContent = (
        <SignatureChip
          document={fileCellDocumentData.document}
          signature={fileCellDocumentData.document.signature}
        />
      )
    } else if (tableColumnHeader.displayImagesInline) {
      fileContent = <FileInlineImage document={fileCellDocumentData.document} />
    } else {
      fileContent = (
        <FileChip
          document={fileCellDocumentData.document}
          fileName={fileCellDocumentData.document.name}
          fileUrl={getWebPath({
            objectId: fileCellDocumentData.document.id,
            objectType: BreadcrumbObjectType.DOCUMENT,
            isUnauth: !user,
          })}
        />
      )
    }
  }
  const textWrap = maxColWidth >= TEXT_LENGTH_LIMIT ? styles.textWrapping : {}

  const formattedCellValue = (
    <FormattedTableCellComponent
      activeUsers={activeUsers}
      cell={cell}
      fileContent={fileContent}
      maxColWidth={maxColWidth}
      readOnly={readOnly}
      showAutogeneratedIdTooltip={showAutogeneratedIdTooltip}
      styles={styles}
      tableColumnHeader={tableColumnHeader}
      textWrap={textWrap}
      value={value}
    />
  )

  const webPath = getWebPath({
    objectType: BreadcrumbObjectType.TABLE_ROW,
    objectId: rowId,
  })

  return (
    <If condition={isFirstColumn}>
      <Then>
        <Box sx={styles.indexCell}>
          {tableType === TableType.TABLE ||
          tableType === TableType.BACKLINK_TABLE ||
          (tableType === TableType.CHART && tableSource === TableSource.PIVOT_TABLE_DRILLDOWN) ? (
            <Link
              className="flex gap-1"
              href={webPath}
              onClick={(e) => e.stopPropagation()}
              sx={styles.indexCell.textClickable}
              target={tableSource === TableSource.PIVOT_TABLE_DRILLDOWN ? '_blank' : undefined}
              underline="hover"
            >
              <Box className="invisible group-hover/row:visible" sx={styles.indexCell.linkOut}>
                <FiExternalLink />
              </Box>
              <Tooltip arrow disableInteractive leaveDelay={0} title={`Go to record`}>
                <Box>{formattedCellValue}</Box>
              </Tooltip>
            </Link>
          ) : (
            formattedCellValue
          )}
        </Box>
      </Then>
      <Else>{formattedCellValue}</Else>
    </If>
  )
}
